import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge with the client design-system roles (tailwind.config.js). Without it `text-ca-meta` would be read as
 * a text COLOR and dropped next to `text-ca-muted`.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['ca-display', 'ca-title', 'ca-heading', 'ca-body-lg', 'ca-body', 'ca-meta', 'ca-caption'] }],
      leading: [{ leading: ['ca-normal', 'ca-tight'] }],
      tracking: [{ tracking: ['ca-label'] }],
      rounded: [{ rounded: ['ca-sm', 'ca-md', 'ca-lg'] }],
      'rounded-t': [{ 'rounded-t': ['ca-sm', 'ca-md', 'ca-lg'] }],
      shadow: [
        {
          shadow: [
            'ca-gold-ring', 'ca-select-ring', 'ca-selectable', 'ca-gold-halo', 'ca-gold-outline', 'ca-gold-edge', 'ca-accent-ring',
            'ca-avatar-ring', 'ca-focus', 'ca-lift', 'ca-dot', 'ca-photo-ring', 'ca-photo-hair', 'ca-total', 'ca-dock', 'ca-seg',
            'ca-plan', 'ca-plan-draft', 'ca-sheet', 'ca-dialog', 'ca-drawer', 'ca-toast',
          ],
        },
      ],
      'bg-image': [{ bg: ['ca-shade-top', 'ca-shade-media', 'ca-shade-hero', 'ca-gold-card', 'ca-logo', 'ca-photo-grad'] }],
      'max-w': [{ 'max-w': ['ca-reading', 'ca-reading-inset', 'ca-catalog'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Detect template type based on event date
 * Friday, Saturday, Sunday → weekend
 * Monday-Thursday → weekday
 */
export function detectTemplateType(date: Date): 'weekday' | 'weekend' {
  const day = date.getDay();
  // 0 = Sunday, 5 = Friday, 6 = Saturday
  if (day === 0 || day === 5 || day === 6) {
    return 'weekend';
  }
  return 'weekday';
}

/**
 * Format date to YYYY-MM-DD for API
 */
export function formatDateForAPI(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Format date to display string
 */
export function formatDateDisplay(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format currency (Colombian Pesos)
 * @param amount - The amount to format
 * @param options - Optional Intl.NumberFormat options to override defaults
 */
export function formatCurrency(
  amount: number,
  options?: Partial<Intl.NumberFormatOptions>
): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(amount);
}

/**
 * Format discount percentage to 2 decimal places
 * Examples:
 * - "73.0171709%" → "73.02%"
 * - "10%" → "10.00%"
 * - "5.5%" → "5.50%"
 * - 10 → "10.00%"
 */
export function formatDiscountPercentage(discount: string | number): string {
  // If it's a number, just format it
  if (typeof discount === 'number') {
    return `${discount.toFixed(2)}%`;
  }

  // Extract the number from strings like "73.0171709%" or "10%"
  const match = discount.match(/(\d+\.?\d*)/);
  if (!match) return discount;

  const numValue = parseFloat(match[1]);
  return `${numValue.toFixed(2)}%`;
}

/**
 * Capitalize first letter of each word in a name
 * Examples:
 * - "maria" → "Maria"
 * - "maria jose" → "Maria Jose"
 * - "JUAN CARLOS" → "Juan Carlos"
 * - "de la cruz" → "De La Cruz"
 */
export function capitalizeName(name: string): string {
  if (!name) return '';

  return name
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word) => {
      if (word.length === 0) return '';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Couple display label from optional bride/groom names.
 * - "María & José" when both are present
 * - "María" when only the bride is recorded
 * - null when there is no bride name (nothing to show)
 */
export function coupleName(names: {
  bride_name?: string;
  groom_name?: string;
}): string | null {
  if (!names.bride_name) return null;
  return names.groom_name
    ? `${names.bride_name} & ${names.groom_name}`
    : names.bride_name;
}

/**
 * Format phone number for display
 * Combines country code and phone number, or displays legacy format as-is
 *
 * Examples:
 * - formatPhoneDisplay("3001234567", "+57") → "+57 3001234567"
 * - formatPhoneDisplay("3001234567") → "3001234567" (legacy)
 * - formatPhoneDisplay("+52 123 456 7890") → "+52 123 456 7890" (legacy with country code)
 */
export function formatPhoneDisplay(phone: string, countryCode?: string): string {
  // New format: combine country code + phone
  if (countryCode) {
    return `${countryCode} ${phone}`;
  }

  // Legacy format: display exactly as stored
  return phone;
}

/**
 * Parse phone number for editing in PhoneInput component
 * Handles both legacy and new formats
 *
 * Examples:
 * - parsePhoneForEdit("3001234567", "+57") → "+573001234567"
 * - parsePhoneForEdit("+52 1234567890") → "+521234567890" (legacy)
 * - parsePhoneForEdit("3001234567") → "+573001234567" (legacy, default to Colombia)
 */
export function parsePhoneForEdit(phone: string, countryCode?: string): string {
  // New format: combine country code + phone
  if (countryCode) {
    // Remove any spaces and combine
    return `${countryCode}${phone.replace(/\s/g, '')}`;
  }

  // Legacy: if already has +, use it; otherwise default to Colombia
  const cleanPhone = phone.replace(/\s/g, '');
  return cleanPhone.startsWith('+') ? cleanPhone : `+57${cleanPhone}`;
}

/**
 * Extract files from a drag event.
 * Tries dataTransfer.files first (desktop files), then falls back to
 * dataTransfer.items (images dragged from web pages like WhatsApp Web).
 */
/**
 * Extract files from a drag event.
 * Tries dataTransfer.files first (desktop files), then falls back to
 * dataTransfer.items (images dragged from web pages like WhatsApp Web).
 */
export function getFilesFromDragEvent(e: React.DragEvent): File[] {
  const files = Array.from(e.dataTransfer.files);
  if (files.length > 0) return files;
  if (e.dataTransfer.items) {
    for (const item of Array.from(e.dataTransfer.items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  }
  return files;
}

/**
 * Extract files from a clipboard paste event.
 * Handles images pasted from web pages (e.g. right-click → Copy Image in WhatsApp Web).
 */
export function getFilesFromPasteEvent(e: React.ClipboardEvent): File[] {
  const files: File[] = [];
  if (e.clipboardData.items) {
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
  }
  return files;
}

// ---------------------------------------------------------------------------
// Type-check helpers
// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-explicit-any */
export function isNonEmptyString(v: any): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

export function isSetNumber(v: any): boolean {
  return v !== null && v !== undefined && typeof v === 'number';
}

export function isSetBoolean(v: any): boolean {
  return v !== null && v !== undefined && typeof v === 'boolean';
}

export function isNonEmptyArray(v: any): boolean {
  return Array.isArray(v) && v.length > 0;
}

/** Returns true if an object has at least one non-null/non-empty leaf value. */
export function hasNonEmptyObjectValue(obj: any): boolean {
  if (obj === null || obj === undefined || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number') return true;
    if (typeof v === 'boolean') return true;
    if (v !== null && v !== undefined) return true;
    return false;
  });
}
/* eslint-enable @typescript-eslint/no-explicit-any */
